import type { GridSortModel } from "@mui/x-data-grid-premium";
import {
  AreaItemType,
  Ordering,
  SortType,
  type PivotColumnResponse,
  type ReportField,
  type SortField,
} from "../../../../../../../../api/types/svTypes";
import { isCustomColumn } from "../../../../utilities/fieldsHelper";
import { getDataColumns } from "../renderDisplayValue";

export const pivotSortableAggregationTypes = [SortType.Measure, SortType.Custom];
/**
 * Rebuild sorting fields based on new sorting model.
 * @param newSorting - New sorting model. As far as we can't remove a sorting from the grid,
 * sorting change can't be triggered with empty sorting model.
 * @param values - Measures.
 * @param columnsResponse - Columns response.
 * @param currentSorts - Current sorts.
 * @returns Rebuilt sorting fields.
 */
export const rebuildPivotSortingFields = (
  newSorting: GridSortModel,
  values: ReportField[],
  columnsResponse: PivotColumnResponse[],
  currentSorts: SortField[]
) => {
  if (columnsResponse.length === 0) {
    return currentSorts;
  }
  const flattenColumns = columnsResponse.flatMap(getDataColumns);
  //Ensure that sorting is in the response columns.
  const sanitizedNewSorting = newSorting.filter((s) => flattenColumns.find((c) => c.id === s.field));
  //As far as we single measure there is always one sort.
  if (sanitizedNewSorting.length === 1) {
    const targetSortColumn = flattenColumns.find((c) => c.id === sanitizedNewSorting[0]?.field);
    if (targetSortColumn && targetSortColumn.id) {
      const field = values.find((f) => f.config.guid === targetSortColumn.measureGuid);
      if (!field) {
        return currentSorts;
      }
      const columnId = targetSortColumn.id;
      if (currentSorts.some((c) => c.config.pivotColumnId === columnId)) {
        //Toggle sort case
        return structuredClone(currentSorts).map((c) => {
          if (c.config.pivotColumnId === columnId) {
            return {
              ...c,
              config: {
                ...c.config,
                ordering: c.config.ordering === Ordering.Ascending ? Ordering.Descending : Ordering.Ascending,
              },
            };
          }
          return c;
        });
      } else {
        return [
          ...currentSorts.filter((s) => !pivotSortableAggregationTypes.includes(s.config.type)),
          createSortingField(field, columnId),
        ];
      }
    }
  }
  return currentSorts.filter((s) => !pivotSortableAggregationTypes.includes(s.config.type));
};

const createSortingField = (field: ReportField, pivotColumnId: string) => {
  return {
    meta: field.meta,
    config: {
      name: field.meta.name,
      id: field.config.guid,
      ordering: Ordering.Ascending,
      caption: field.config.customLabel || field.meta.caption,
      type: isCustomColumn(field) ? SortType.Custom : SortType.Measure,
      pivotColumnId,
    },
    areaItemType: AreaItemType.SORTS,
  } as SortField;
};
